/**
 * @architecture-header 1
 * @layer module
 * @domain assistants
 * @responsibility Describe the persona fields a teacher tunes and summarise a persona for pickers.
 * @state-owner dataset store holds the saved personas
 * @allowed-imports shared view types and demo constants
 * @forbidden React; network; store implementations
 * @public-api assistants module
 * @side-effects none
 * @tests modules/assistants/model/assistant.test.ts
 */

import type {
  AssistantAnswerPolicy,
  AssistantEmojiUse,
  AssistantRecord,
  AssistantTone,
} from '../../../shared/lib/model';

export const TONE_LABEL: Readonly<Record<AssistantTone, string>> = {
  warm: '溫暖鼓勵',
  calm: '冷靜清楚',
  playful: '幽默陪伴',
  coach: '嚴格教練',
};

export const TONE_HINT: Readonly<Record<AssistantTone, string>> = {
  warm: '先接住情緒，再帶下一步',
  calm: '不寒暄，把理由講清楚',
  playful: '用比喻和玩笑降低壓力',
  coach: '要求說出依據，節奏快',
};

export const EMOJI_USE_LABEL: Readonly<Record<AssistantEmojiUse, string>> = {
  none: '不用',
  light: '偶爾',
  lots: '很多',
};

export const ANSWER_POLICY_LABEL: Readonly<Record<AssistantAnswerPolicy, string>> = {
  never: '永遠不給答案',
  'after-hints': '提示用完才給',
};

/** The four dials, each written as the two ends of the scale the teacher is choosing between. */
export interface TraitDefinition {
  readonly key: 'warmth' | 'humour' | 'strictness' | 'brevity';
  readonly label: string;
  readonly low: string;
  readonly high: string;
}

export const TRAITS: readonly TraitDefinition[] = [
  { key: 'warmth', label: '溫暖', low: '就事論事', high: '先安撫再教' },
  { key: 'humour', label: '幽默', low: '正經', high: '愛開玩笑' },
  { key: 'strictness', label: '嚴格', low: '順著學生走', high: '要求說出理由' },
  { key: 'brevity', label: '簡潔', low: '講得很細', high: '一兩句就停' },
];

/** Reads a dial back as words, which is how it reaches the model prompt. */
export function traitWord(value: number, trait: TraitDefinition): string {
  if (value >= 70) return trait.high;
  if (value <= 30) return trait.low;
  return `介於${trait.low}與${trait.high}之間`;
}

/** Avatar palette. Enough spread that three personas never look like the same character. */
export const ACCENTS: readonly string[] = [
  '#6b38d4',
  '#2f6f8f',
  '#d9772e',
  '#0f9d76',
  '#c2410c',
  '#7c3f8f',
  '#3f51b5',
  '#b03a5b',
];

/** How long a persona description can get before it stops being read as a whole. */
export const PERSONA_LIMIT = 600;

export function blankAssistant(id: string): AssistantRecord {
  return {
    id,
    revision: 1,
    name: '',
    emoji: '🤖',
    accent: ACCENTS[0],
    tagline: '',
    persona: '',
    tone: 'warm',
    warmth: 60,
    humour: 40,
    strictness: 50,
    brevity: 60,
    address: '同學',
    catchphrase: '',
    greeting: '',
    emojiUse: 'light',
    hintsBeforeTeacher: 3,
    answerPolicy: 'never',
    forbidden: '',
    replyRules: [],
    isDefault: false,
  };
}

export function findAssistant(
  assistants: readonly AssistantRecord[],
  id: string | null | undefined,
): AssistantRecord | undefined {
  return id ? assistants.find((item) => item.id === id) : undefined;
}

export function defaultAssistant(
  assistants: readonly AssistantRecord[],
): AssistantRecord | undefined {
  return assistants.find((item) => item.isDefault) ?? assistants[0];
}

/** The assistant a run answers with: whatever it was assigned, else the current default. */
export function assistantForRun(
  assistants: readonly AssistantRecord[],
  assistantId: string | null | undefined,
): AssistantRecord | undefined {
  return findAssistant(assistants, assistantId) ?? defaultAssistant(assistants);
}

/** One line for pickers and headers: 溫暖鼓勵 · 提示 3 次後找老師. */
export function assistantSummary(assistant: AssistantRecord): string {
  return `${TONE_LABEL[assistant.tone]} · 提示 ${assistant.hintsBeforeTeacher} 次後找老師`;
}
